import { createContext, useContext, useState, useEffect } from 'react';
import { SHOPIFY_CONFIG, getShopifyClient } from '../config/shopify';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [checkoutId, setCheckoutId] = useState(null);

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const addToCart = (product) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.title === product.title);
      if (existingItem) {
        return prevCart.map(item =>
          item.title === product.title
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, { ...product, quantity: 1 }];
    });
    setIsCartOpen(true);
  };

  const removeFromCart = (title) => {
    setCart(prevCart => prevCart.filter(item => item.title !== title));
  };

  const updateQuantity = (title, quantity) => {
    if (quantity <= 0) {
      removeFromCart(title);
      return;
    }
    setCart(prevCart =>
      prevCart.map(item =>
        item.title === title ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    setCheckoutUrl(null);
    setCheckoutId(null);
  };

  const checkout = async () => {
    if (!SHOPIFY_CONFIG.enabled || cart.length === 0) return;
    
    setIsLoading(true);
    try {
      const client = await getShopifyClient();
      
      // Fetch all products to get variant IDs
      const products = await client.product.fetchAll(50);
      console.log('Available Shopify products:', products.map(p => p.title));
      
      // Build line items
      const lineItems = [];
      const notFound = [];
      
      for (const cartItem of cart) {
        const searchTitle = cartItem.title.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
        const product = products.find(p => {
          const pTitle = p.title.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
          // Try multiple matching strategies
          return pTitle.includes(searchTitle) || 
                 searchTitle.includes(pTitle) ||
                 // Match "3pack" to "3 pack" or "3-pack"
                 pTitle.replace(/(\d)/, '$1 ').includes(searchTitle.replace(/(\d)/, '$1 ')) ||
                 // Match bundle numbers
                 (searchTitle.includes('3pack') && pTitle.includes('3')) ||
                 (searchTitle.includes('6pack') && pTitle.includes('6')) ||
                 (searchTitle.includes('12pack') && pTitle.includes('12'));
        });
        
        if (product) {
          // For bundles with flavors, add as custom attributes
          const lineItem = {
            variantId: product.variants[0].id,
            quantity: cartItem.quantity,
          };
          
          // Add flavor selections as custom attributes if present
          if (cartItem.flavors && cartItem.flavors.length > 0) {
            lineItem.customAttributes = cartItem.flavors.map((flavor, idx) => ({
              key: `Flavor ${idx + 1}`,
              value: flavor
            }));
          }
          
          lineItems.push(lineItem);
        } else {
          notFound.push(cartItem.title);
          console.error(`Product not found: "${cartItem.title}". Available: ${products.map(p => p.title).join(', ')}`);
        }
      }
      
      if (lineItems.length === 0) {
        console.error('No products found for checkout. Cart items:', cart.map(c => c.title));
        // Show error to user
        const { toast } = await import('sonner');
        toast.error('Unable to checkout', {
          description: `Products not found in store: ${notFound.join(', ')}. Please contact us for assistance.`
        });
        setIsLoading(false);
        return;
      }
      
      // Create checkout
      const checkout = await client.checkout.create();
      await client.checkout.addLineItems(checkout.id, lineItems);
      
      // Redirect to Shopify checkout
      window.location.href = checkout.webUrl;
      
    } catch (error) {
      console.error('Checkout error:', error);
      const { toast } = await import('sonner');
      toast.error('Checkout failed', {
        description: 'Please try again or contact us for assistance.'
      });
    }
    setIsLoading(false);
  };

  return (
    <CartContext.Provider value={{
      cart,
      cartCount,
      cartTotal,
      isCartOpen,
      setIsCartOpen,
      isLoading,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      checkout,
    }}>
      {children}
    </CartContext.Provider>
  );
};
